import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Tooltip,
} from "@altimateai/lego";
import { IconLink, IconUnlink } from "@tabler/icons-react";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { persistLineageSettings } from "../api";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { setRefSourceEnabled, setShowRefs } from "../context/LineageSlice";
import { dedupeAndFilterRefs, SOURCE_DOT_COLORS } from "../utils";
import { Ref, RefSource } from "../types";
import { cn } from "../../../shared/utils";

interface SourceConfig {
  source: RefSource;
  label: string;
  description: string;
}

const SOURCE_ORDER: SourceConfig[] = [
  {
    source: "test",
    label: "Declared from tests",
    description: "FK relationships from `relationships` data tests",
  },
  {
    source: "contract",
    label: "From model contracts",
    description: "FK constraints declared on enforced model contracts",
  },
  {
    source: "semantic",
    label: "From semantic entities",
    description: "Foreign↔primary entity pairings in semantic models",
  },
  {
    source: "inferred",
    label: "Inferred from naming",
    description: "Heuristic FK detection from `<x>_id` column patterns",
  },
];

/**
 * ERD overlay toolbar control. Renders as a single button portaled into
 * `#refs-container`; clicking opens a popover with:
 *   - master Show/Hide toggle (`showRefs`)
 *   - per-source checkboxes with counts
 *
 * Gated behind `aiEnabled` — same Altimate API key gate used by other
 * premium lineage features.
 */
const RefsToggleButton = () => {
  const container = document.getElementById("refs-container");
  const lineageDispatch = useLineageDispatch();
  const {
    state: { showRefs, refs, aiEnabled, enabledRefSources, inferenceConfidenceThreshold },
  } = useLineageContext();

  // Per-source counts use raw refs (one per source per FK pair) — useful
  // for "this source contributes N refs". Deduped count is the number of
  // unique relationships actually rendered after collapsing overlap.
  const counts = useMemo(() => countsBySource(Object.values(refs)), [refs]);
  const dedupedCount = useMemo(() => {
    if (!showRefs) return 0;
    return dedupeAndFilterRefs(Object.values(refs), enabledRefSources, inferenceConfidenceThreshold)
      .length;
  }, [refs, showRefs, enabledRefSources, inferenceConfidenceThreshold]);
  const totalUniqueRelationships = useMemo(() => {
    // Same as deduped count but ignoring per-source toggles — the universe
    // of distinct relationships across all sources.
    const allEnabled: Record<RefSource, boolean> = {
      test: true,
      contract: true,
      semantic: true,
      inferred: true,
    };
    return dedupeAndFilterRefs(Object.values(refs), allEnabled, 0).length;
  }, [refs]);

  if (!container || !aiEnabled) {
    return null;
  }

  const tooltip = showRefs
    ? `Hide relationships (${dedupedCount} of ${totalUniqueRelationships} shown)`
    : `Show relationships (${totalUniqueRelationships} found)`;

  // Each toggle dispatches the local state change AND persists it via the
  // existing `persistLineageSettings` round-trip so the lens shape survives
  // panel reloads. Best-effort — failures are silently swallowed since the
  // user-visible state is already updated locally.
  const persist = (patch: Parameters<typeof persistLineageSettings>[0]) => {
    persistLineageSettings(patch).catch(() => {});
  };

  return createPortal(
    <Popover>
      <Tooltip title={tooltip}>
        <PopoverTrigger asChild>
          <Button variant={showRefs ? "default" : "outline"} size="xs">
            {showRefs ? <IconLink size={16} /> : <IconUnlink size={16} />}
            Relationships
            {showRefs && dedupedCount > 0 ? (
              // Light translucent chip so the count reads on top of the
              // primary-filled button (variant="default"). bg-primary/20
              // on a primary button was primary-on-primary — invisible.
              <span className="al-ml-1.5 al-inline-flex al-items-center al-rounded-full al-bg-white/25 al-px-1.5 al-text-[10px] al-font-semibold al-leading-none al-h-4">
                {dedupedCount}
              </span>
            ) : null}
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" className="al-w-72 al-tw-scope">
        <div className="al-flex al-items-center al-justify-between al-pb-2">
          <span className="al-text-sm al-font-semibold">Relationships overlay</span>
          <Switch
            checked={showRefs}
            onCheckedChange={value => {
              lineageDispatch(setShowRefs(value));
              persist({ showRefs: value });
            }}
            aria-label="Show or hide all relationship edges"
          />
        </div>

        <div
          className={cn(
            "al-flex al-flex-col al-gap-2 al-border-t al-pt-2",
            !showRefs && "al-opacity-50 al-pointer-events-none"
          )}
        >
          {SOURCE_ORDER.map(({ source, label, description }) => (
            <label
              key={source}
              htmlFor={`ref-source-${source}`}
              className="al-flex al-items-start al-gap-2 al-cursor-pointer"
              title={description}
            >
              <Checkbox
                id={`ref-source-${source}`}
                checked={enabledRefSources[source]}
                onCheckedChange={value => {
                  const enabled = Boolean(value);
                  lineageDispatch(setRefSourceEnabled({ source, enabled }));
                  persist({
                    enabledRefSources: { ...enabledRefSources, [source]: enabled },
                  });
                }}
                aria-label={label}
              />
              <span className="al-flex-1 al-text-xs al-flex al-items-center al-gap-1.5">
                {/* Color swatch matches the dot used in edge hover indicators
                    so users can read the on-canvas hover panel without a
                    separate legend. */}
                <span
                  className="al-inline-block al-rounded-full al-flex-shrink-0"
                  style={{
                    width: 8,
                    height: 8,
                    backgroundColor: SOURCE_DOT_COLORS[source],
                  }}
                  aria-hidden
                />
                <span>{label}</span>
                <span className="al-ml-1 al-text-muted-foreground">({counts[source]})</span>
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>,
    container
  );
};

function countsBySource(refs: Ref[]): Record<RefSource, number> {
  const out: Record<RefSource, number> = {
    test: 0,
    contract: 0,
    semantic: 0,
    inferred: 0,
  };
  for (const ref of refs) {
    out[ref.source] = (out[ref.source] ?? 0) + 1;
  }
  return out;
}

export default RefsToggleButton;
